import { useCallback, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import { bulkTeamManagementChannelsQuery } from 'graphql-queries';
import { PageSizeType } from 'design-system-web';

export type PermissionQueryType = 'IN' | 'NOT_IN';

type FilterState = {
  keyword: string;
  groupRoleIds: string[];
  parentChannelIds: string[];
  permissions: string[];
  permissionQueryType: PermissionQueryType;
  page: number;
  pageSize: PageSizeType;
};

const INITIAL_STATE: FilterState = {
  keyword: '',
  groupRoleIds: [],
  parentChannelIds: [],
  permissions: [],
  permissionQueryType: 'IN',
  page: 0,
  pageSize: 25,
};

/**
 * Manages the search/filter/table state for the bulk team permission
 * management landing page. Every filter change re-queries
 * `bulkTeamManagementChannelsQuery` server-side (via `search` variables) and
 * resets pagination back to the first page.
 */
export function useBulkTeamManagementChannels() {
  const [filters, setFilters] = useState<FilterState>(INITIAL_STATE);
  const [selectedChannelIds, setSelectedChannelIds] = useState<string[]>([]);

  const search = useMemo(() => {
    const s: Record<string, unknown> = {};

    if (filters.keyword) {
      s.name = { type: 'like', value: filters.keyword };
    }

    if (filters.groupRoleIds.length) {
      s.groupRoleIds = filters.groupRoleIds;
    }

    if (filters.parentChannelIds.length) {
      s.parentChannelIds = filters.parentChannelIds;
    }

    if (filters.permissions.length) {
      s.permissions = filters.permissions;
      s.permissionQueryType = filters.permissionQueryType;
    }

    return s;
  }, [filters]);

  const { data, loading, refetch } = useQuery(bulkTeamManagementChannelsQuery, {
    fetchPolicy: 'cache-and-network',
    variables: {
      pagination: { start: filters.page * filters.pageSize, perPage: filters.pageSize },
      search,
    },
  });

  const updateFilters = useCallback(
    (update: Partial<Omit<FilterState, 'page' | 'pageSize'>>) => {
      setFilters(prev => ({ ...prev, ...update, page: 0 }));
      setSelectedChannelIds([]);
    },
    []
  );

  const handleKeywordChange = useCallback(
    (keyword: string) => updateFilters({ keyword }),
    [updateFilters]
  );
  const handleGroupRoleIdsChange = useCallback(
    (groupRoleIds: string[]) => updateFilters({ groupRoleIds }),
    [updateFilters]
  );
  const handleParentChannelIdsChange = useCallback(
    (parentChannelIds: string[]) => updateFilters({ parentChannelIds }),
    [updateFilters]
  );
  const handlePermissionsChange = useCallback(
    (permissions: string[]) => updateFilters({ permissions }),
    [updateFilters]
  );
  const handlePermissionQueryTypeChange = useCallback(
    (permissionQueryType: PermissionQueryType) =>
      updateFilters({ permissionQueryType }),
    [updateFilters]
  );
  const handlePageChange = useCallback(
    (page: number) => setFilters(prev => ({ ...prev, page })),
    []
  );
  const handlePageSizeChange = useCallback((pageSize: PageSizeType) => {
    setFilters(prev => ({ ...prev, pageSize, page: 0 }));
    setSelectedChannelIds([]);
  }, []);

  return {
    channels: data?.bulkModuleChannels?.items ?? [],
    total: data?.bulkModuleChannels?.pageInfo?.total ?? 0,
    loading,
    page: filters.page,
    pageSize: filters.pageSize,
    selectedChannelIds,
    setSelectedChannelIds,
    handleKeywordChange,
    handleGroupRoleIdsChange,
    handleParentChannelIdsChange,
    handlePermissionsChange,
    handlePermissionQueryTypeChange,
    handlePageChange,
    handlePageSizeChange,
    refetch,
  };
}
